/**
 * 記事一覧ページ (Phase 2 実 API 接続版).
 *
 * admin-api の `GET /sites/:siteId/posts` に実接続し、フィルタ + 削除を実装。
 * 新規作成と編集は PostEditPage で対応。
 *
 * ゴミ箱タブ:
 *   - GET /posts?trash=true で deleted_at IS NOT NULL の記事一覧を取得
 *   - 復元ボタン: POST /posts/:id/restore
 *   - 完全削除ボタン (owner のみ表示): DELETE /posts/:id?hard=true
 *
 * Phase 3:
 *   - Figma 完成デザインへ全面置換 (docs/figma-progress.md #18, #55, #56)
 *   - バルク操作、検索、カテゴリ/タグ フィルタ
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { memo, useCallback, useEffect, useState } from 'react';
import { PlanLimitBannerConnected } from '../components/PlanLimitBannerConnected.js';
import {
  authMe,
  deletePost,
  fetchSiteMembers,
  fetchSitePosts,
  hardDeletePost,
  type PostRow,
  restorePost,
} from '../lib/api-client.js';
import { formatErrorMessage, isRevConflict } from '../lib/error-messages.js';
import { trackEvent } from '../lib/posthog.js';

const STATUS_LABELS: Record<PostRow['status'], string> = {
  draft: '下書き',
  scheduled: '予約投稿',
  published: '公開済',
  unlisted: '非公開',
  trash: 'ゴミ箱',
};

const STATUS_BADGE_STYLES: Record<PostRow['status'], { bg: string; text: string }> = {
  published: { bg: '#ecfdf5', text: '#065f46' },
  draft: { bg: '#fffbeb', text: '#92400e' },
  unlisted: { bg: '#f2f1f0', text: '#706d65' },
  scheduled: { bg: '#eff6ff', text: '#1e40af' },
  trash: { bg: '#fef2f2', text: '#991b1b' },
};

type StatusFilter = PostRow['status'] | 'all';
type TabKind = 'active' | 'trash';

/**
 * 30 日後の自動削除予定日を人間可読な日付文字列で返す。
 * deletedAt に 30 日を加算して日付部分だけ整形。
 */
function formatPurgeDate(deletedAt: number): string {
  const purgeAt = deletedAt + 30 * 24 * 60 * 60 * 1000;
  return new Date(purgeAt).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

// QD-25: 行アイテムを memo 化して大量記事時のレンダリングを削減
interface PostRowProps {
  post: PostRow;
  siteId: string;
  tab: TabKind;
  isOwner: boolean;
  deletePending: boolean;
  restorePending: boolean;
  hardDeletePending: boolean;
  onDelete: (post: PostRow) => void;
  onRestore: (post: PostRow) => void;
  onRequestHardDelete: (post: PostRow) => void;
}

const PostRowItem = memo(function PostRowItem({
  post,
  siteId,
  tab,
  isOwner,
  deletePending,
  restorePending,
  hardDeletePending,
  onDelete,
  onRestore,
  onRequestHardDelete,
}: PostRowProps) {
  const statusStyle = STATUS_BADGE_STYLES[post.status];
  const statusLabel = STATUS_LABELS[post.status];
  const dateText =
    tab === 'trash' && post.deletedAt
      ? formatPurgeDate(post.deletedAt)
      : new Date(post.updatedAt).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });

  return (
    <tr className="border-b border-[#edebe8] hover:bg-[#f8f7f6]">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          className="rounded border border-[#d6d3d0]"
          disabled
          aria-hidden="true"
          tabIndex={-1}
        />
      </td>
      <td className="px-4 py-3">
        {tab === 'trash' ? (
          <span className="text-sm font-medium text-[#706d65]">{post.title}</span>
        ) : (
          <Link
            to="/sites/$siteId/posts/$postId/edit"
            params={{ siteId, postId: post.id }}
            className="text-sm font-medium text-[#23221e] hover:text-[#3b82f6]"
          >
            {post.title}
          </Link>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-[#706d65]">/{post.slug}</td>
      <td className="px-4 py-3">
        <span
          className="inline-block rounded-full px-3 py-1 text-xs font-medium"
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
        >
          {statusLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-[#706d65]">{dateText}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-4">
          {tab === 'active' ? (
            <>
              <Link
                to="/sites/$siteId/posts/$postId/edit"
                params={{ siteId, postId: post.id }}
                className="text-xs font-medium text-[#3b82f6] hover:text-[#1d4ed8]"
              >
                編集
              </Link>
              <button
                type="button"
                onClick={() => onDelete(post)}
                disabled={deletePending}
                className="text-xs font-medium text-[#3b82f6] hover:text-[#1d4ed8] disabled:text-[#c1bdb7]"
              >
                {deletePending ? '...' : '削除'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onRestore(post)}
                disabled={restorePending}
                className="text-xs font-medium text-[#3b82f6] hover:text-[#1d4ed8] disabled:text-[#c1bdb7]"
              >
                {restorePending ? '...' : '復元'}
              </button>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => onRequestHardDelete(post)}
                  disabled={hardDeletePending}
                  className="text-xs font-medium text-[#991b1b] hover:text-[#7f1d1d] disabled:text-[#c1bdb7]"
                >
                  完全削除
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
});

export function PostsPage() {
  const params = useParams({ strict: false }) as { siteId?: string };
  const siteId = params.siteId ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const PAGE_SIZE = 50;
  const [tab, setTab] = useState<TabKind>('active');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [pendingHardDelete, setPendingHardDelete] = useState<PostRow | null>(null);

  // 300ms debounce でサーバ検索
  useEffect(() => {
    const trimmed = searchQuery.trim();
    const t = setTimeout(() => {
      setDebouncedQuery(trimmed);
      setOffset(0);
      if (trimmed) {
        trackEvent('posts_searched', { siteId, length: trimmed.length });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, siteId]);

  // タブ切替時は offset / filter をリセット
  useEffect(() => {
    setOffset(0);
    if (tab === 'trash') {
      setFilter('all');
    }
  }, [tab]);

  const isTrash = tab === 'trash';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sites', siteId, 'posts', tab, filter, debouncedQuery, offset],
    queryFn: () =>
      fetchSitePosts(siteId, {
        ...(isTrash ? { trash: true } : filter === 'all' ? {} : { status: filter }),
        ...(debouncedQuery ? { q: debouncedQuery } : {}),
        limit: PAGE_SIZE,
        offset,
      }),
    enabled: !!siteId,
  });

  // owner 判定: hard delete ボタンを出すかの判断に使う
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authMe,
    enabled: !!siteId && isTrash,
  });
  const membersQuery = useQuery({
    queryKey: ['sites', siteId, 'members'],
    queryFn: () => fetchSiteMembers(siteId),
    enabled: !!siteId && isTrash,
  });
  const currentRole = membersQuery.data?.find((m) => m.userId === meQuery.data?.id)?.role;
  const isOwner = currentRole === 'owner';

  const deleteMutation = useMutation({
    mutationFn: ({ postId, rev }: { postId: string; rev: number }) =>
      deletePost(siteId, postId, rev),
    onSuccess: () => {
      trackEvent('post_deleted', { siteId });
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'posts'] });
    },
    onError: async (e) => {
      if (isRevConflict(e)) {
        await queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'posts'] });
      }
    },
  });

  const restoreMutation = useMutation({
    mutationFn: ({ postId }: { postId: string }) => restorePost(siteId, postId),
    onSuccess: (_, variables) => {
      trackEvent('post_restored', { siteId, postId: variables.postId });
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'posts'] });
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: ({ postId }: { postId: string }) => hardDeletePost(siteId, postId),
    onSuccess: (_, variables) => {
      trackEvent('post_hard_deleted', { siteId, postId: variables.postId });
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'posts'] });
      setPendingHardDelete(null);
    },
    onError: () => {
      setPendingHardDelete(null);
    },
  });

  // QD-25: useCallback で stable 参照にすることで PostRowItem.memo の再レンダリング抑制
  const handleDelete = useCallback(
    (post: PostRow) => {
      if (!window.confirm(`「${post.title}」をゴミ箱に移動しますか？`)) return;
      deleteMutation.mutate({ postId: post.id, rev: post.rev });
    },
    [deleteMutation],
  );

  const handleRestore = useCallback(
    (post: PostRow) => {
      restoreMutation.mutate({ postId: post.id });
    },
    [restoreMutation],
  );

  const handleHardDeleteConfirmed = useCallback(() => {
    if (!pendingHardDelete) return;
    hardDeleteMutation.mutate({ postId: pendingHardDelete.id });
  }, [pendingHardDelete, hardDeleteMutation]);

  if (isLoading) return <div className="text-sm text-[#706d65]">読み込み中...</div>;

  if (error) {
    const msg = formatErrorMessage(error, '記事の読み込みに失敗しました');
    return (
      <div className="rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-4 text-sm text-[#991b1b]">
        <p className="font-medium">記事の読み込みに失敗しました</p>
        <p className="mt-1 text-xs">{msg}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-[6px] bg-[#3b82f6] px-4 py-2 text-sm font-medium text-white hover:bg-[#1d4ed8]"
        >
          再読み込み
        </button>
      </div>
    );
  }

  const posts = data?.posts ?? [];
  const total = data?.total ?? 0;
  const filteredPosts = posts;

  return (
    <div className="space-y-6">
      {/* プラン上限バナー: Free のみ posts=100 制限、他プランは unlimited で非表示 */}
      <PlanLimitBannerConnected siteId={siteId} resource="posts" />

      {/* Tabs: 公開中・下書き / ゴミ箱 */}
      <div
        className="flex gap-1 border-b border-[#edebe8]"
        role="tablist"
        aria-label="記事の表示切替"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'active'}
          onClick={() => setTab('active')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'active'
              ? 'border-b-2 border-[#3b82f6] text-[#3b82f6]'
              : 'text-[#706d65] hover:text-[#23221e]'
          }`}
        >
          公開中・下書き
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'trash'}
          onClick={() => setTab('trash')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'trash'
              ? 'border-b-2 border-[#3b82f6] text-[#3b82f6]'
              : 'text-[#706d65] hover:text-[#23221e]'
          }`}
        >
          ゴミ箱
        </button>
      </div>

      {/* Top Area: Button + Search */}
      <div className="flex items-center justify-between gap-4">
        {tab === 'active' ? (
          <button
            type="button"
            onClick={() => navigate({ to: '/sites/$siteId/posts/new', params: { siteId } })}
            className="rounded-[6px] bg-[#3b82f6] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#1d4ed8]"
          >
            + 新規投稿
          </button>
        ) : (
          <p className="text-xs text-[#706d65]">
            ゴミ箱の記事は 30 日後に自動で完全削除されます。復元ボタンで戻せます。
          </p>
        )}
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            placeholder="🔍 タイトルで検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-[6px] border border-[#d6d3d0] bg-white px-4 py-2.5 text-sm text-[#23221e] placeholder-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
          />
        </div>
      </div>

      {/* Status Filter Dropdown (active タブのみ) */}
      {tab === 'active' && (
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm text-[#706d65]">
            ステータス:
          </label>
          <select
            id="status-filter"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value as StatusFilter);
              setOffset(0);
            }}
            className="rounded-[6px] border border-[#d6d3d0] bg-white px-3 py-2 text-sm text-[#23221e] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
          >
            <option value="all">すべて</option>
            <option value="draft">下書き</option>
            <option value="scheduled">予約投稿</option>
            <option value="published">公開済</option>
            <option value="unlisted">非公開</option>
          </select>
        </div>
      )}

      {/* Table Card */}
      <div className="rounded-[8px] border border-[#edebe8] bg-white shadow-sm">
        <table className="w-full">
          {/* Table Header */}
          <thead>
            <tr className="border-b border-[#edebe8] bg-[#edebe8]">
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  className="rounded border border-[#d6d3d0]"
                  disabled
                  aria-hidden="true"
                  tabIndex={-1}
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#706d65]">
                タイトル
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#706d65]">
                カテゴリ
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#706d65]">
                ステータス
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#706d65]">
                {tab === 'trash' ? '自動削除予定' : '更新日'}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#706d65]">
                操作
              </th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {filteredPosts.map((post) => (
              <PostRowItem
                key={post.id}
                post={post}
                siteId={siteId}
                tab={tab}
                isOwner={isOwner}
                deletePending={deleteMutation.isPending}
                restorePending={restoreMutation.isPending}
                hardDeletePending={hardDeleteMutation.isPending}
                onDelete={handleDelete}
                onRestore={handleRestore}
                onRequestHardDelete={setPendingHardDelete}
              />
            ))}
          </tbody>
        </table>

        {/* Empty State */}
        {filteredPosts.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-[#706d65]">
            {debouncedQuery
              ? `「${debouncedQuery}」に一致する記事がありません`
              : tab === 'trash'
                ? 'ゴミ箱は空です'
                : filter === 'all'
                  ? 'まだ記事がありません。「+ 新規投稿」から作成してください'
                  : `${STATUS_LABELS[filter]} の記事はありません`}
          </div>
        )}
      </div>

      {/* Info Footer + Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#706d65]">
          {debouncedQuery ? `「${debouncedQuery}」の検索結果: ${total} 件` : `全 ${total} 件`}{' '}
          {total > PAGE_SIZE ? `(${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} 件目)` : ''}
        </div>
        {total > PAGE_SIZE && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="rounded-[6px] border border-[#d6d3d0] bg-white px-3 py-2 text-sm font-medium text-[#23221e] hover:bg-[#f8f7f6] disabled:cursor-not-allowed disabled:text-[#c1bdb7]"
            >
              前へ
            </button>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="rounded-[6px] border border-[#d6d3d0] bg-white px-3 py-2 text-sm font-medium text-[#23221e] hover:bg-[#f8f7f6] disabled:cursor-not-allowed disabled:text-[#c1bdb7]"
            >
              次へ
            </button>
          </div>
        )}
      </div>

      {/* Hard Delete 確認ダイアログ (軽量な独自実装、AlertDialog 依存パッケージを増やさない) */}
      {pendingHardDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hard-delete-title"
        >
          <div className="w-full max-w-md rounded-[8px] bg-white p-6 shadow-lg">
            <h2
              id="hard-delete-title"
              className="text-base font-semibold tracking-[0.03em] text-[#23221e]"
            >
              記事を完全削除しますか？
            </h2>
            <p className="mt-3 text-sm text-[#706d65]">
              「{pendingHardDelete.title}」を完全削除します。
              <br />
              <span className="font-medium text-[#991b1b]">
                この操作は取り消せません。履歴 (rev) や紐付いたカテゴリも削除されます。
              </span>
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingHardDelete(null)}
                disabled={hardDeleteMutation.isPending}
                className="rounded-[6px] border border-[#d6d3d0] bg-white px-4 py-2 text-sm font-medium text-[#23221e] hover:bg-[#f8f7f6]"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleHardDeleteConfirmed}
                disabled={hardDeleteMutation.isPending}
                className="rounded-[6px] bg-[#991b1b] px-4 py-2 text-sm font-medium text-white hover:bg-[#7f1d1d] disabled:bg-[#c1bdb7]"
              >
                {hardDeleteMutation.isPending ? '削除中...' : '完全削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
